/** Keep tablet rendering within a pixel budget without changing simulation speed. */
export function useMobileQuality(userAgent: string, touchPoints: number, override: string | null): boolean {
  if (override === 'high') return false
  if (override === 'mobile') return true
  // iPadOS often identifies itself as a Macintosh when requesting desktop sites.
  return /Android|iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh|MacIntel/i.test(userAgent) && touchPoints > 1)
}

export class RenderQuality {
  private scale = 1
  private seconds = 0
  private frames = 0
  private warmup = 3
  private fastWindows = 0
  constructor(readonly mobile: boolean) {}

  pixelRatio(width: number, height: number, deviceRatio: number): number {
    const budget = this.mobile ? 3_200_000 : 3_000_000
    const ceiling = Math.min(deviceRatio, this.mobile ? 1.5 : 2, Math.sqrt(budget / Math.max(1, width * height)))
    // Keep Retina tablets above CSS resolution, even under sustained load.
    // The previous .55 floor made a large iPad render at roughly 700px wide.
    const floor = this.mobile ? Math.min(ceiling, 1.25) : 0
    return Math.max(floor, ceiling * this.scale)
  }

  /** Raw frame time, before the physics catch-up clamp. Ignore tab suspension. */
  sample(dt: number): boolean {
    if (!Number.isFinite(dt) || dt <= 0 || dt > .5) { this.resetSampling(); return false }
    if (this.warmup > 0) { this.warmup -= dt; return false }
    this.seconds += dt; this.frames++
    if (this.seconds < 2) return false
    const fps = this.frames / this.seconds
    this.seconds = 0; this.frames = 0
    const before = this.scale
    if (fps < 28) { this.scale = Math.max(this.mobile ? .85 : .55, this.scale - .05); this.fastWindows = 0 }
    else if (fps > (this.mobile ? 29.5 : 50)) {
      if (++this.fastWindows >= 4) { this.scale = Math.min(1, this.scale + .05); this.fastWindows = 0 }
    } else this.fastWindows = 0
    return Math.abs(before - this.scale) > .001
  }

  resetSampling(): void {
    this.seconds = 0; this.frames = 0; this.warmup = 3; this.fastWindows = 0
  }
}
